import React, { useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import markdownit from "markdown-it";
import { Typography, message } from "antd";
import { getImageUrlByScene } from "../utils/urlUtils";
import "./CombinedMessageCard.css";

// Initialize markdown-it with HTML and line breaks enabled
const md = markdownit({ html: true, breaks: true });

export interface CombinedMessageCardProps {
    markdownContent?: string;
    qrcodeContent?: string;
    className?: string;
}

/**
 * 尝试修复可能被编码错误的中文文本
 * @param text 可能被编码错误的文本
 * @returns 尝试修复后的文本
 */
const tryToFixEncodedText = (text: string): string => {
    // 尝试检测是否为UTF-8编码问题（æ‚¨çš„é这种格式）
    if (/æ|ç|š|è|‚/.test(text)) {
        try {
            // 尝试将文本作为被编码两次的文本处理
            return decodeURIComponent(escape(text));
        } catch (e) {
            console.warn("Failed to decode text:", e);
            // 失败后返回原始文本
            return text;
        }
    }
    return text;
};

/**
 * 渲染Markdown内容
 * @param content Markdown格式的文本
 * @returns 渲染后的React元素
 */
const renderMarkdown = (content: string) => {
    return (
        <Typography>
            <div dangerouslySetInnerHTML={{ __html: md.render(content) }} />
        </Typography>
    );
};

/**
 * 组合消息卡片组件 - 专门处理文本和二维码的混合显示
 */
const CombinedMessageCard: React.FC<CombinedMessageCardProps> = ({
    markdownContent = "",
    qrcodeContent = "",
    className = "",
}) => {
    // 尝试修复可能被编码错误的文本
    const fixedMarkdownContent = useMemo(
        () => tryToFixEncodedText(markdownContent),
        [markdownContent]
    );

    // 长按相关状态和引用
    const [isLongPressing, setIsLongPressing] = useState(false);
    const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
    const touchStartTimeRef = useRef<number>(0);

    // 下载图片功能
    const downloadImage = async () => {
        try {
            const imageUrl = getImageUrlByScene();
            const response = await fetch(imageUrl);
            const blob = await response.blob();

            // 创建下载链接
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = '关爱通福利顾问二维码.jpg';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            message.success('二维码已保存到本地');
        } catch (error) {
            console.error('保存图片失败:', error);
        }
    };

    // 长按开始
    const handleTouchStart = (e: React.TouchEvent) => {
        e.preventDefault();
        touchStartTimeRef.current = Date.now();
        setIsLongPressing(false);

        longPressTimerRef.current = setTimeout(() => {
            setIsLongPressing(true);
            // 触发触觉反馈（如果支持）
            if (navigator.vibrate) {
                navigator.vibrate(50);
            }
        }, 500); // 500ms后触发长按
    };

    // 长按结束
    const handleTouchEnd = (e: React.TouchEvent) => {
        e.preventDefault();
        const touchDuration = Date.now() - touchStartTimeRef.current;

        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }

        if (isLongPressing && touchDuration >= 500) {
            downloadImage();
        }

        setIsLongPressing(false);
    };

    // 取消长按（触摸移动时）
    const handleTouchMove = (e: React.TouchEvent) => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
        setIsLongPressing(false);
    };

    // 桌面端右键菜单处理
    const handleContextMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        downloadImage();
    };

    return (
        <div className={`combined-message-card ${className}`}>
            {markdownContent && (
                <div className="combined-text-content">
                    {qrcodeContent ? (
                        <p>{fixedMarkdownContent}</p>
                    ) : (
                        renderMarkdown(fixedMarkdownContent)
                    )}
                </div>
            )}

            {qrcodeContent && (
                <div className="combined-qrcode-content">
                    <img
                        src={getImageUrlByScene()}
                        alt="关爱通福利顾问"
                        style={{
                            width: '200px',
                            height: '200px',
                            display: 'block',
                            margin: '16px',
                            cursor: 'pointer',
                            userSelect: 'none',
                            borderRadius: '8px',
                            transition: 'opacity 0.2s, transform 0.2s',
                            opacity: isLongPressing ? 0.8 : 1,
                            transform: isLongPressing ? 'scale(0.95)' : 'scale(1)',
                        }}
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                        onTouchMove={handleTouchMove}
                        onContextMenu={handleContextMenu}
                        draggable={false}
                    />

                </div>
            )}
        </div>
    );
};

export default CombinedMessageCard; 